import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {WithChildren} from '../../../../_metronic/helpers'
import { getAuth, onAuthStateChanged, User, AuthError, MultiFactorResolver, RecaptchaVerifier } from "firebase/auth";
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { logger } from '../../../utils/logger';

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined | null 
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined | null>>
  logout: () => Promise<boolean>
  // MFA support
  mfaResolver: MultiFactorResolver | null
  setMfaResolver: Dispatch<SetStateAction<MultiFactorResolver | null>>
  mfaVerificationId: string | null
  setMfaVerificationId: Dispatch<SetStateAction<string | null>>
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined, 
  setCurrentUser: () => {},
  logout: async () => false,
  // MFA support
  mfaResolver: null,
  setMfaResolver: () => {},
  mfaVerificationId: null,
  setMfaVerificationId: () => {},
}

/**
 * Must match SYNC_MARKER_KEY in utils/pushNotifications.ts (private there).
 * Clearing it forces the next sign-in to re-register this device instead of
 * trusting a "registered recently" throttle that belonged to the last session.
 */
const PUSH_SYNC_MARKER_KEY = 'wc.push.sync'

/**
 * Release this device's web-push subscription once nobody is signed in.
 *
 * WHY: the FCM subscription belongs to the DEVICE, not the session. Left in
 * place after a sign-out (manual, or the idle-session timeout) the service
 * worker keeps showing "a client emailed you" notifications - customer name and
 * subject - and re-setting the icon badge on a locked phone with nobody signed
 * in. Every sign-out path ends in onAuthStateChanged(null), so the provider is
 * the single choke point.
 *
 * WHY no disablePush() / authenticated unregister: the ID token is already gone
 * here. The server prunes the dead token on its next failed send (FCM rejects
 * an unsubscribed endpoint), and the next sign-in re-acquires a token through
 * syncPushTokenIfGranted.
 *
 * WHY not sdk.deleteToken(): without a preceding getToken(swRegistration) it
 * registers a SECOND default service worker (scope /firebase-cloud-messaging-
 * push-scope) and unsubscribes THAT registration - not the one our token lives
 * on. Unsubscribing the real PushManager subscription is the authoritative kill,
 * needs no SDK load for signed-out visitors, and the SDK self-heals on the next
 * getToken (subscription mismatch -> delete old token, mint a new one).
 *
 * Fire-and-forget and idempotent (a signed-out cold start and a second tab both
 * land here too). NEVER throws and never touches a token, so nothing to log.
 */
export async function releasePushOnSignOut(): Promise<void> {
  // Synchronous and unconditional: even if the browser work below fails, the
  // next sign-in must not be throttled by the previous session's marker.
  try { localStorage.removeItem(PUSH_SYNC_MARKER_KEY) } catch { /* private mode / blocked storage */ }
  try {
    // The OS icon badge was last set by the service worker for the previous
    // user's mail; clear it while we are in a window that can.
    const badgeClear = (navigator as any)?.clearAppBadge?.()
    if (badgeClear && typeof badgeClear.catch === 'function') badgeClear.catch(() => { /* unsupported / not installed */ })
  } catch { /* best effort */ }
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const registration = await navigator.serviceWorker.getRegistration('/')
    const subscription = await registration?.pushManager?.getSubscription()
    if (!subscription) return
    // Somebody signed in while we were looking (shared phone, quick account
    // switch): the subscription is theirs now - leave it, their sync owns it.
    if (getAuth().currentUser) return
    await subscription.unsubscribe()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[push] sign-out release failed (${(e as any)?.name || 'error'})`)
  }
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined | null>(undefined)
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null)
  const [mfaVerificationId, setMfaVerificationId] = useState<string | null>(null)
  const db = getFirestore();

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = async (): Promise<boolean> => {
    const currentUserId = currentUser?.id; 
    const currentUserEmail = currentUser?.email;

    console.log('[AuthProvider.logout] Attempting to log LOGOUT_INITIATED...');
    await logger.info('Logout initiated via AuthProvider.', { 
      source: 'AuthProvider.logout', 
      module: 'Authentication', 
      action: 'LOGOUT_INITIATED',
      userId: currentUserId,
      userEmail: currentUserEmail
    });
    console.log('[AuthProvider.logout] LOGOUT_INITIATED log call completed.');

    const authInstance = getAuth();
    try {
      await authInstance.signOut(); 
      console.log('[AuthProvider.logout] Firebase signOut successful.');
      return true; // Firebase sign-out successful
    } catch (error: any) { 
      console.log('[AuthProvider.logout] Firebase signOut failed.', error);
      await logger.error('Firebase sign out error in AuthProvider.', error, { 
        source: 'AuthProvider.logout', 
        module: 'Authentication', 
        action: 'LOGOUT_AUTH_FAILURE',
        userId: currentUserId,
        userEmail: currentUserEmail,
        errorCode: error.code
      });
      return false;
    }
  }

  useEffect(() => {
    const authInstance = getAuth();
    const unsubscribe = onAuthStateChanged(authInstance, async (user: User | null) => {
      if (user) {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data() as UserModel;
            // Ensure userData conforms to UserModel, especially if uid/email are not top-level in your Firestore doc
            // For logging, we'll use user.uid and userData.email (assuming email is on UserModel)
            const effectiveUserForContext: UserModel = {
                ...userData, // Spread existing data from Firestore
                id: user.uid, // Explicitly ensure uid from Firebase Auth is present and mapped to id
                email: user.email || userData.email || '' // Prioritize Firebase Auth email, then Firestore, then empty string
            };
            
            if (currentUser?.id !== user.uid || !currentUser) {
              logger.info('User context set after fetching Firestore profile.', {
                source: 'AuthProvider.onAuthStateChanged',
                module: 'Authentication',
                action: 'LOGIN_CONTEXT_SUCCESS',
                userId: user.uid,
                userEmail: effectiveUserForContext.email
              });
            } else {
              logger.debug('User context refreshed (no change in user ID).', {
                source: 'AuthProvider.onAuthStateChanged',
                module: 'Authentication',
                action: 'LOGIN_CONTEXT_REFRESHED',
                userId: user.uid,
                userEmail: effectiveUserForContext.email
              });
            }
            setCurrentUser(effectiveUserForContext);
          } else {
            logger.error(`User authenticated with Firebase but Firestore document missing. UID: ${user.uid}`, null, {
              source: 'AuthProvider.onAuthStateChanged',
              module: 'Authentication',
              action: 'LOGIN_FIRESTORE_DOC_MISSING',
              userId: user.uid,
              userEmail: user.email || undefined 
            });
            setCurrentUser({ id: user.uid, email: user.email || '' } as UserModel); 
          }
        } catch (error: any) { // Catch as any for broader compatibility, or specific error type
           logger.error('Error fetching user data from Firestore in onAuthStateChanged.', error, {
             source: 'AuthProvider.onAuthStateChanged',
             module: 'Authentication',
             action: 'LOGIN_FIRESTORE_FETCH_FAILURE',
             userId: user.uid,
             errorCode: error.code
           });
           setCurrentUser(null);
        }
      } else {
        console.log('[AuthProvider.onAuthStateChanged] User is null. Processing logout context.');
        // First, and not awaited: it must survive a throwing logger call below
        // and must not hold up clearing the session context.
        void releasePushOnSignOut();
        const previousUser = currentUser;
        console.log('[AuthProvider.onAuthStateChanged] previousUser:', previousUser);
        if (previousUser !== undefined && previousUser !== null) { 
          console.log('[AuthProvider.onAuthStateChanged] previousUser is valid. Attempting to log LOGOUT_CONTEXT_SUCCESS...');
          const previousUserId = previousUser?.id;
          const previousUserEmail = previousUser?.email;
          await logger.info('User is now signed out and context is cleared.', { 
            source: 'AuthProvider.onAuthStateChanged', 
            module: 'Authentication', 
            action: 'LOGOUT_CONTEXT_SUCCESS',
            userId: previousUserId,
            userEmail: previousUserEmail
          });
          console.log('[AuthProvider.onAuthStateChanged] LOGOUT_CONTEXT_SUCCESS log call completed.');
        } else {
          console.log('[AuthProvider.onAuthStateChanged] previousUser is null or undefined. Skipping LOGOUT_CONTEXT_SUCCESS.');
        }
        setCurrentUser(null);
        authHelper.removeAuth();
        // Clear the SEPARATE identity keys the login paths write, so a logout
        // leaves no stale user/uid/role/profile behind on the device.
        ['user', 'user_uid', 'role', 'display_name', 'profile_image_url', 'email'].forEach((k) => {
          try { localStorage.removeItem(k); } catch { /* ignore */ }
        });
      }
    });

    return () => {
      unsubscribe();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [db]); // Removed currentUser from deps to prevent re-runs when previousUser logic is enough

  return (
    currentUser === undefined ? (
      <LayoutSplashScreen />
    ) : (
      <AuthContext.Provider value={{ auth, saveAuth, currentUser, setCurrentUser, logout, mfaResolver, setMfaResolver, mfaVerificationId, setMfaVerificationId }}>
        {children}
      </AuthContext.Provider>
    )
  );
}

export {AuthProvider, useAuth}
